/**
 * Cliente HTTP compartido entre los módulos por dominio de `lib/api/`.
 *
 * Sprint 18.4.1 (P1-5 audit, mayo 20, 2026): extraído del antiguo
 * archivo monolítico `lib/api.ts` (1405 LOC). Acá quedan solo los
 * primitivos que todos los dominios necesitan:
 *
 * - `API_V1`: base URL del API.
 * - `request<T>()`: wrapper async-fetch con auth + manejo de errores.
 * - `getAccessToken()`: extracción del JWT de Supabase.
 *
 * **Convención del nombre con underscore prefix**: este archivo NO se
 * exporta desde `index.ts` y los call sites externos no deben
 * importarlo directamente. Es "package private" para módulos hermanos.
 * TypeScript no enforcea esto — depende del code review.
 */

import { apiErrorFromResponse, safeFetch } from "../errors";
import { supabase } from "../supabase";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";
export const API_V1 = `${API_BASE}/v1`;

export async function getAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export async function request<T>(path: string, init?: Parameters<typeof fetch>[1]): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const res = await safeFetch(`${API_V1}${path}`, {
    ...init,
    headers,
  });

  if (res.status === 401) {
    // Token inválido o expirado y refresh falló. Limpia sesión local
    // para que el Router redirija a /login.
    await supabase.auth.signOut();
  }

  if (!res.ok) {
    throw await apiErrorFromResponse(res);
  }

  // 204 No Content no trae body (aunque FastAPI mande `content-type:
  // application/json`): parsearlo lanzaba `Unexpected end of JSON input`
  // y la UI trataba como fallida una operación ya aplicada (issue #575).
  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}
